import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Container,
  Paper,
  Snackbar,
  InputAdornment,
  IconButton,
  Checkbox,
  FormControlLabel,
  Link
} from '@mui/material';
import { LockOutlined, EmailOutlined, Visibility, VisibilityOff } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useFlashMessage } from '../../hooks/useFlashMessage';
import { isMobileDevice } from '../../utils/mobileDetection';

const LoginForm = () => {
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const { login, isAuthenticated, permissionsReady } = useAuth();
  const navigate = useNavigate();
  const { showError, showSuccess, message, type, isVisible, hideMessage } = useFlashMessage();

  // Redirect to appropriate page only after authenticated AND permissions are loaded
  useEffect(() => {
    if (isAuthenticated && permissionsReady) {
      // Check if user is on mobile device and redirect to count history view
      if (isMobileDevice()) {
        navigate('/inventory-count-history', { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    }
  }, [isAuthenticated, permissionsReady, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCredentials(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent multiple rapid submissions
    if (isSubmitting || loading) {
      return;
    }
    
    setIsSubmitting(true);
    setLoading(true);
    setError('');

    try {
      const result = await login(credentials.username, credentials.password);
      
      if (!result.success) {
        const errorMsg = result.error || 'Login failed';
        setError(errorMsg);
        showError(errorMsg);
      } else {
        showSuccess('Login successful! Redirecting...');
        // Navigation will happen automatically via useEffect when isAuthenticated AND permissionsReady are both true
      }
    } catch (err) {
      const errorMsg = 'An unexpected error occurred';
      setError(errorMsg);
      showError(errorMsg);
    } finally {
      setLoading(false);
      setIsSubmitting(false);
    }
  };


  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Left Column - Background Image */}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          width: '60%',
          position: 'relative',
          backgroundImage: 'url(/img/golf-hero-bg.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      >
        {/* Dark Overlay */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.3) 0%, rgba(0, 0, 0, 0.5) 100%)',
            zIndex: 1
          }}
        />
        
        {/* Branding Text - Centered */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 2,
            color: 'white',
            textAlign: 'center',
            width: '100%',
            px: 4
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <Typography
              variant="h2"
              component="h1"
              sx={{
                fontWeight: 700,
                fontSize: { md: '3rem', lg: '3.5rem' },
                color: 'white',
                textShadow: '2px 2px 4px rgba(0, 0, 0, 0.5)'
              }}
            >
              Malbon VietNam WMS
            </Typography>
          </motion.div>
        </Box>
      </Box>

      {/* Right Column - Login Form */}
      <Box
        sx={{
          width: { xs: '100%', md: '40%' },
          minHeight: '100vh',
          backgroundColor: '#fdfdfc',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 3, sm: 4, md: 6 }
        }}
      >
        <Container maxWidth="sm" sx={{ width: '100%' }}>
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {/* Logo */}
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center' }}>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.5, delay: 0.4, type: "spring", stiffness: 200 }}
              >
                <img
                  src="/img/malbon-logo.png"
                  alt="Malbon Logo"
                  style={{
                    maxWidth: '380px',
                    maxHeight: '380px',
                    objectFit: 'contain'
                  }}
                />
              </motion.div>
            </Box>

            {/* Welcome Message */}
            <Box sx={{ mb: 4, textAlign: 'center' }}>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: 700,
                  color: '#1e293b',
                  mb: 1,
                  fontSize: { xs: '1.75rem', sm: '2rem' }
                }}
              >
                Welcome Back
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: '#64748b',
                  fontSize: '1rem'
                }}
              >
                Log in to the warehouse management system
              </Typography>
            </Box>

            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <Alert
                  severity="error"
                  sx={{
                    mb: 3,
                    borderRadius: 1,
                    backgroundColor: '#fef2f2',
                    border: '1px solid #fecaca'
                  }}
                >
                  {error}
                </Alert>
              </motion.div>
            )}

            {/* Login Form */}
            <Box component="form" onSubmit={handleSubmit} sx={{ mb: 3 }}>
              {/* Email Field */}
              <Box sx={{ mb: 2.5 }}>
                <Typography
                  variant="body2"
                  sx={{
                    mb: 1,
                    color: '#1e293b',
                    fontWeight: 500
                  }}
                >
                  Email
                </Typography>
                <TextField
                  fullWidth
                  name="username"
                  value={credentials.username}
                  onChange={handleInputChange}
                  variant="outlined"
                  disabled={loading || isSubmitting}
                  autoComplete="email"
                  placeholder="name@malbon.com"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailOutlined sx={{ color: '#64748b' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      backgroundColor: '#ffffff',
                      '& fieldset': {
                        borderColor: '#e2e8f0'
                      },
                      '&:hover fieldset': {
                        borderColor: '#36704b'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#36704b',
                        borderWidth: '2px'
                      }
                    }
                  }}
                />
              </Box>

              {/* Password Field */}
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="body2"
                  sx={{
                    mb: 1,
                    color: '#1e293b',
                    fontWeight: 500
                  }}
                >
                  Password
                </Typography>
                <TextField
                  fullWidth
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={credentials.password}
                  onChange={handleInputChange}
                  variant="outlined"
                  disabled={loading || isSubmitting}
                  autoComplete="current-password"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlined sx={{ color: '#64748b' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          size="small"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      backgroundColor: '#ffffff',
                      '& fieldset': {
                        borderColor: '#e2e8f0'
                      },
                      '&:hover fieldset': {
                        borderColor: '#36704b'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#36704b',
                        borderWidth: '2px'
                      }
                    }
                  }}
                />
              </Box>

              {/* Remember Me & Forgot Password */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 3
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      sx={{
                        color: '#36704b',
                        '&.Mui-checked': {
                          color: '#36704b'
                        }
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ color: '#1e293b' }}>
                      Remember me
                    </Typography>
                  }
                />
                <Link
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    showError('Forgot password functionality is not yet implemented');
                  }}
                  sx={{
                    color: '#36704b',
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }}
                >
                  Forgot password?
                </Link>
              </Box>

              {/* Login Button */}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading || isSubmitting || !credentials.username || !credentials.password}
                sx={{
                  height: 48,
                  fontSize: '1rem',
                  fontWeight: 600,
                  backgroundColor: '#36704b',
                  color: '#ffffff',
                  borderRadius: '8px',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#2d5a3d'
                  },
                  '&:disabled': {
                    backgroundColor: '#e2e8f0',
                    color: '#94a3b8'
                  }
                }}
              >
                {loading || isSubmitting ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Log In'
                )}
              </Button>
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* Flash message snackbar */}
      <Snackbar
        open={isVisible}
        autoHideDuration={5000}
        onClose={hideMessage}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={hideMessage}
          severity={type}
          sx={{
            width: '100%',
            borderRadius: 1,
            boxShadow: '0 8px 16px rgba(0, 0, 0, 0.15)'
          }}
        >
          {message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LoginForm;